import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';
import { format, startOfMonth, endOfMonth, parseISO } from 'date-fns';
import { payrollService } from '@/lib/payroll';
import {
  MonthlyPayrollData,
  PayrollCalculation,
  PayrollConstants,
} from '@/types/slovak-payroll';
import { formatEUR, formatPayrollPeriod } from '@/lib/payroll';
import { employeeToSlovakEmployee, isSlovakCompliantEmployee, validateSlovakEmployee, getSlovakComplianceCompleteness, getContractTypeDisplayName } from '@/lib/employees';
import { usePayrollTranslation, PayrollLanguage } from '@/lib/payroll';
import { useSlovakEmployees, useEmployeesNeedingSlovakSetup } from '@/hooks/employees';
import { usePlanFulfillment, EmployeePlanFulfillment } from '@/hooks/employees/usePlanFulfillment';
import { BusinessDetails } from '@/types/index';
import { generateSimplePayslip as generatePayslipPDF, PayslipData } from '@/lib/payroll/pdf-generator';
import { EmployeePayrollTable } from './EmployeePayrollTable';
import { getPayslipPassword } from '@/lib/payroll/employee-preferences';
import { PaymentProgressSummary } from './PaymentProgressSummary';
import { PayrollStatusAlerts } from './PayrollStatusAlerts';
import {
  PaymentStageId,
  PaymentItem,
  getStageFromMonthStatus,
  getStageFromPaymentStatus
} from './payments/types';
import { usePayrollPayments, PayrollPayment } from '@/hooks/connect/usePayrollPayments';
import { useFeatureGating } from '@/hooks/business/useFeatureGating';

// Enhanced payroll components and utilities
import { PayrollPeriodSelector, ConsolidatedPayrollStats } from './PayrollPeriodSelector';
import { getBusinessPlanningSummary, EmployeePlannedHours } from '@/lib/payroll/planning/shift-planning-utils';
import { PayrollHistoryProvider, usePayrollHistory } from '@/context/PayrollHistoryContext';
import { useMonthStatuses } from '@/hooks/payroll/useMonthStatuses';
import { 
  PayrollFinalizationConfirmation,
  PaymentProcessingConfirmation,
  SecurityConfirmationDialog
} from './PayrollSecurityDialogs';
import { usePayrollAudit } from '@/hooks/payroll/usePayrollAudit';
import {
  PayrollPeriod,
  MonthStatus,
  PayrollAction,
  PayrollStatus
} from '@/types/payroll-periods';
import {
  createPayrollPeriod,
  createMonthStatus
} from '@/lib/payroll/month-status-utils';

// Payment availability utility functions
interface PaymentAvailability {
  qrCodesAvailable: boolean;
  qrCodesBulkAvailable: boolean;
  stripePaymentsAvailable: boolean;
  readOnlyMode: boolean;
  showPaymentWarning: boolean;
  paymentWarningMessage?: string;
  userFriendlyState: 'FUTURE' | 'UPCOMING' | 'IN_PROGRESS' | 'TO_BE_PROCESSED' | 'PROCESSED';
}

const getPaymentAvailabilityForStatus = (
  monthStatus: MonthStatus | null,
  payrollRecordsExist: boolean
): PaymentAvailability => {
  // Default state when no month status available
  if (!monthStatus) {
    return {
      qrCodesAvailable: payrollRecordsExist,
      qrCodesBulkAvailable: payrollRecordsExist,
      stripePaymentsAvailable: payrollRecordsExist,
      readOnlyMode: false,
      showPaymentWarning: false,
      userFriendlyState: 'IN_PROGRESS'
    };
  }

  // Map month status to user-friendly states
  const getUserFriendlyState = (monthStatus: MonthStatus): PaymentAvailability['userFriendlyState'] => {
    if (monthStatus.type === 'future' || monthStatus.type === 'future_planning') {
      return 'FUTURE';
    } else if (monthStatus.type === 'current_planning') {
      return 'UPCOMING';
    } else if (monthStatus.type === 'current_incomplete') {
      return 'IN_PROGRESS';
    } else if (monthStatus.type === 'last_complete') {
      return 'TO_BE_PROCESSED';
    } else {
      return 'PROCESSED'; // historical, locked
    }
  };

  const userFriendlyState = getUserFriendlyState(monthStatus);
  const isLocked = monthStatus.status === 'locked' || monthStatus.status === 'paid';
  
  switch (userFriendlyState) {
    case 'FUTURE':
      return {
        qrCodesAvailable: false,
        qrCodesBulkAvailable: false,
        stripePaymentsAvailable: false,
        readOnlyMode: true,
        showPaymentWarning: true,
        paymentWarningMessage: 'Payments will be available after payroll is calculated',
        userFriendlyState: 'FUTURE'
      };
      
    case 'UPCOMING':
      return {
        qrCodesAvailable: false,
        qrCodesBulkAvailable: false,
        stripePaymentsAvailable: false,
        readOnlyMode: true,
        showPaymentWarning: true,
        paymentWarningMessage: 'Payments will be available when the month begins',
        userFriendlyState: 'UPCOMING'
      };
      
    case 'IN_PROGRESS':
      return {
        qrCodesAvailable: payrollRecordsExist,
        qrCodesBulkAvailable: payrollRecordsExist,
        stripePaymentsAvailable: false,
        readOnlyMode: false,
        showPaymentWarning: payrollRecordsExist,
        paymentWarningMessage: 'These are preliminary payment amounts. Wait until month end for final processing.',
        userFriendlyState: 'IN_PROGRESS'
      };
      
    case 'TO_BE_PROCESSED':
      return {
        qrCodesAvailable: payrollRecordsExist,
        qrCodesBulkAvailable: payrollRecordsExist,
        stripePaymentsAvailable: payrollRecordsExist && (monthStatus.status === 'finalized' || monthStatus.status === 'calculated'),
        readOnlyMode: isLocked,
        showPaymentWarning: false,
        userFriendlyState: 'TO_BE_PROCESSED'
      };
      
    case 'PROCESSED':
      return {
        qrCodesAvailable: payrollRecordsExist && !isLocked,
        qrCodesBulkAvailable: false,
        stripePaymentsAvailable: false,
        readOnlyMode: true,
        showPaymentWarning: isLocked,
        paymentWarningMessage: isLocked ? 'This period is locked and payments have been processed' : 'This historical period is view-only',
        userFriendlyState: 'PROCESSED'
      };
      
    default:
      return {
        qrCodesAvailable: payrollRecordsExist,
        qrCodesBulkAvailable: payrollRecordsExist,
        stripePaymentsAvailable: payrollRecordsExist,
        readOnlyMode: false,
        showPaymentWarning: false,
        userFriendlyState: 'IN_PROGRESS'
      };
  }
};

interface PayrollRecord {
  id: string;
  employee_id: string;
  employee_name: string;
  actual_hours: number;
  gross_pay: number;
  net_pay: number;
  calculation_method: string;
  country_code: string | null;
  auto_calculated: boolean;
  created_at: string;
  status: string;
}

interface EmployeeWithPayroll {
  employee_id: string;
  employee_name: string;
  role_name?: string;
  pay_rate: number;
  actual_hours: number;
  gross_pay: number;
  net_pay: number;
  calculation_method: string;
  country_code: string | null;
  auto_calculated: boolean;
  status: string;
  has_payroll_record: boolean;
  payroll_record_id?: string;
  created_at?: string;
}

interface UnifiedPayrollDashboardProps {
  business: BusinessDetails | null;
  loadingData: boolean;
  countryCode?: string | null;
}

// Internal component without provider
const UnifiedPayrollDashboardInternal: React.FC<UnifiedPayrollDashboardProps> = ({
  business,
  loadingData,
  countryCode = null
}) => {
  // Use business.country_code directly instead of country detection
  const effectiveCountryCode = business?.country_code || countryCode;
  const { t, language, changeLanguage } = usePayrollTranslation('en');
  const [selectedPeriod, setSelectedPeriod] = useState<PayrollPeriod>(() => createPayrollPeriod(new Date()));
  const [payrollRecords, setPayrollRecords] = useState<PayrollRecord[]>([]);
  const [employeesWithPayroll, setEmployeesWithPayroll] = useState<EmployeeWithPayroll[]>([]);
  const [loading, setLoading] = useState(false);
  const [autoCalculating, setAutoCalculating] = useState(false);
  const [constants, setConstants] = useState<PayrollConstants | null>(null);
  const [selectedRecords, setSelectedRecords] = useState<Set<string>>(new Set());
  const [hasExistingPayroll, setHasExistingPayroll] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  // Planning data state - keeping for internal calculation
  const [planningSummary, setPlanningSummary] = useState<{
    employeesWithShifts: number;
    totalEmployees: number;
    totalPlannedHours: number;
    totalProjectedGrossPay: number;
    overScheduledEmployees: number;
    underScheduledEmployees: number;
  } | null>(null);
  const [complianceRate, setComplianceRate] = useState<number>(0);
  const [paymentSummary, setPaymentSummary] = useState<{
    totalEmployeePayments: number;
    totalEmployees: number;
    pendingPayments: number;
    completedPayments: number;
  } | null>(null);

  // Consolidated statistics for period selector - eliminates duplicates
  const [consolidatedStats, setConsolidatedStats] = useState<ConsolidatedPayrollStats | null>(null);
  
  // Enhanced payroll state
  const [monthStatuses, setMonthStatuses] = useState<Map<string, MonthStatus>>(new Map());
  const [currentMonthStatus, setCurrentMonthStatus] = useState<MonthStatus | null>(null);
  const [pendingAction, setPendingAction] = useState<{ action: PayrollAction; period: PayrollPeriod } | null>(null);
  
  // Security dialog states
  const [finalizationDialog, setFinalizationDialog] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [securityDialog, setSecurityDialog] = useState<{ 
    isOpen: boolean; 
    title: string; 
    description: string; 
    action?: () => void;
  }>({ isOpen: false, title: '', description: '' });
  
  // Payroll history and audit
  const payrollHistory = usePayrollHistory();
  const auditService = usePayrollAudit(business?.id || '');

  // Payment integration state
  const [selectedPaymentStage, setSelectedPaymentStage] = useState<PaymentStageId | null>(null);
  const [processingEmployeeId, setProcessingEmployeeId] = useState<string | null>(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrPayments, setQrPayments] = useState<any[]>([]);
  const [qrDialogTitle, setQrDialogTitle] = useState('');

  // Payment hooks
  const payrollPayments = usePayrollPayments(business?.id || '');
  const { hasFeature } = useFeatureGating();
  const hasStripePayments = hasFeature('payment_processing');

  // Use Slovak employees system when applicable
  const { 
    slovakEmployees, 
    allEmployeesWithStatus, 
    loadingSlovakEmployees, 
    refreshSlovakEmployees 
  } = useSlovakEmployees({ businessId: business?.id || null, includeAll: true });
  
  const {
    employeesNeedingSetup,
    needingSetupCount
  } = useEmployeesNeedingSlovakSetup(business?.id || null);

  // Plan fulfillment data - calculates hours from time tracking
  const { fulfillmentData, isLoading: loadingFulfillment } = usePlanFulfillment(business?.id || null);

  // Load constants on mount
  useEffect(() => {
    loadConstants();
  }, []);

  // Create month status when period or payroll data changes
  useEffect(() => {
    if (business?.id && employeesWithPayroll.length >= 0) {
      const totalEmployees = employeesWithPayroll.length;
      const employeesWithRecords = employeesWithPayroll.filter(emp => emp.has_payroll_record).length;
      const totalPayroll = employeesWithPayroll.reduce((sum, employee) => sum + employee.gross_pay, 0);
      
      const monthStatus = createMonthStatus(selectedPeriod, {
        status: hasExistingPayroll ? 'calculated' : 'draft',
        employeeCount: totalEmployees,
        employeesWithPayroll: employeesWithRecords,
        totalGrossPay: totalPayroll,
        totalNetPay: employeesWithPayroll.reduce((sum, emp) => sum + emp.net_pay, 0),
        lastModified: new Date()
      });
      
      setCurrentMonthStatus(monthStatus);
      
      // Update month statuses map
      const key = `${selectedPeriod.year}-${String(selectedPeriod.month).padStart(2, '0')}`;
      setMonthStatuses(prev => new Map(prev.set(key, monthStatus)));
    }
  }, [selectedPeriod, business?.id, employeesWithPayroll, hasExistingPayroll]);

  // Auto-calculate payroll when period or business changes (only if appropriate)
  // DISABLED: RPC function causing 400 errors - needs migration fix
  /*
  useEffect(() => {
    const handleAutoCalculation = async () => {
      if (!business?.id || !initialLoadComplete || !currentMonthStatus) {
        return;
      }

      // Only auto-calculate for certain month types and if no existing payroll
      const shouldAutoCalculate = currentMonthStatus.type !== 'future' &&
                                  currentMonthStatus.canCalculate &&
                                  !hasExistingPayroll;

      if (shouldAutoCalculate) {
        await autoCalculatePayroll();
      }
    };

    handleAutoCalculation();
  }, [selectedPeriod, business?.id, hasExistingPayroll, initialLoadComplete, currentMonthStatus]);
  */

  const loadConstants = async () => {
    try {
      const year = new Date().getFullYear();
      const loadedConstants = await payrollService.getPayrollConstants('SK', year);
      setConstants(loadedConstants);
    } catch (error) {
      toast.error('Error loading payroll constants');
    }
  };

  const autoCalculatePayroll = useCallback(async () => {
    if (!business?.id) return;

    setAutoCalculating(true);
    try {
      const monthStart = startOfMonth(selectedPeriod.date);
      const monthEnd = endOfMonth(selectedPeriod.date);

      // Log the calculation attempt
      await auditService.logCustomAction({
        action: 'calculate',
        period: selectedPeriod,
        description: 'Auto-calculating payroll for period',
        severity: 'medium'
      });

      // Call unified calculation function (now fetches country_code from business table)
      const { data, error } = await supabase.rpc('calculate_unified_payroll_for_period', {
        p_business_id: business.id,
        p_period_start: format(monthStart, 'yyyy-MM-dd'),
        p_period_end: format(monthEnd, 'yyyy-MM-dd')
      });

      if (error) {
        throw error;
      }

      // Load the updated payroll records silently
      await loadEmployeesWithPayroll();

      // Log successful calculation
      const totalPayroll = employeesWithPayroll.reduce((sum, employee) => sum + employee.gross_pay, 0);
      await auditService.logCalculation(
        selectedPeriod,
        employeesWithPayroll.map(emp => emp.employee_id),
        {
          totalGross: totalPayroll,
          totalNet: employeesWithPayroll.reduce((sum, emp) => sum + emp.net_pay, 0),
          calculationMethod: 'auto'
        }
      );

      // No success toast messages - this is automatic background processing
    } catch (error) {
      // Log the error
      await auditService.logCustomAction({
        action: 'calculate',
        period: selectedPeriod,
        description: `Auto-calculation failed: ${error instanceof Error ? error.message : String(error)}`,
        severity: 'high'
      });
      
      toast.error('Failed to calculate payroll', {
        description: error instanceof Error ? error.message : String(error)
      });
    } finally {
      setAutoCalculating(false);
    }
  }, [business?.id, selectedPeriod, countryCode, auditService, employeesWithPayroll]);

  const loadEmployeesWithPayroll = useCallback(async () => {
    if (!business?.id) return;

    setLoading(true);
    try {
      const monthStart = startOfMonth(selectedPeriod.date);
      const monthEnd = endOfMonth(selectedPeriod.date);

      // Use already-fetched employee data instead of duplicate database call
      const employeesData = allEmployeesWithStatus.map(emp => ({
        id: emp.id,
        name: emp.name,
        hourly_rate: emp.hourly_rate,
        roles: emp.roleInfo ? {
          name: emp.roleInfo.name,
          default_pay_rate: emp.roleInfo.default_pay_rate,
          pay_type: emp.roleInfo.pay_type,
          hours_per_month: emp.roleInfo.hours_per_month
        } : null,
        salary_adjustment: emp.salary_adjustment
      }));

      // Then load payroll records for the selected period
      const { data: payrollData, error: payrollError } = await supabase
        .from('payrolls')
        .select(`
          id,
          employee_id,
          actual_hours,
          gross_pay,
          net_pay,
          calculation_method,
          country_code,
          auto_calculated,
          created_at,
          status
        `)
        .eq('business_id', business.id)
        .gte('period_start', format(monthStart, 'yyyy-MM-dd'))
        .lte('period_end', format(monthEnd, 'yyyy-MM-dd'))
        .order('created_at', { ascending: false });

      if (payrollError) {
        throw payrollError;
      }

      // Create map of payroll records by employee_id
      const payrollMap = new Map<string, any>();
      (payrollData || []).forEach(record => {
        payrollMap.set(record.employee_id, record);
      });

      // Combine employee data with payroll data
      const employeesWithPayrollData: EmployeeWithPayroll[] = (employeesData || []).map(employee => {
        const payrollRecord = payrollMap.get(employee.id);
        
        
        // Calculate effective pay rate - prioritize employee's hourly_rate, then role
        let payRate = 0;

        // Priority 1: Employee's direct hourly_rate
        if (employee.hourly_rate && employee.hourly_rate > 0) {
          payRate = employee.hourly_rate;
        }
        // Priority 2: Role's default_pay_rate
        else if (employee.roles?.default_pay_rate) {
          let baseRate = employee.roles.default_pay_rate;

          // Convert monthly salary to hourly rate if needed
          if (employee.roles.pay_type === 'salary') {
            // Use role's hours_per_month, fallback to standard 160 hours/month
            const hoursPerMonth = employee.roles.hours_per_month || 160;
            baseRate = baseRate / hoursPerMonth;
          }

          payRate = baseRate;
        }

        // Apply salary adjustment
        const adjustment = employee.salary_adjustment || 0;
        payRate = Math.max(0, payRate + adjustment);

        if (payrollRecord) {
          return {
            employee_id: employee.id,
            employee_name: employee.name,
            role_name: employee.roles?.name,
            pay_rate: payRate,
            actual_hours: parseFloat(payrollRecord.actual_hours?.toString() || '0'),
            gross_pay: parseFloat(payrollRecord.gross_pay?.toString() || '0'),
            net_pay: parseFloat(payrollRecord.net_pay?.toString() || '0'),
            calculation_method: payrollRecord.calculation_method || 'unknown',
            country_code: payrollRecord.country_code,
            auto_calculated: payrollRecord.auto_calculated || false,
            status: payrollRecord.status || 'draft',
            has_payroll_record: true,
            payroll_record_id: payrollRecord.id,
            created_at: payrollRecord.created_at
          };
        } else {
          return {
            employee_id: employee.id,
            employee_name: employee.name,
            role_name: employee.roles?.name,
            pay_rate: payRate,
            actual_hours: 0,
            gross_pay: 0,
            net_pay: 0,
            calculation_method: 'pending',
            country_code: effectiveCountryCode,
            auto_calculated: false,
            status: 'pending',
            has_payroll_record: false
          };
        }
      });

      // Update legacy payroll records for backward compatibility
      const records: PayrollRecord[] = (payrollData || []).map(record => ({
        id: record.id,
        employee_id: record.employee_id,
        employee_name: employeesData?.find(emp => emp.id === record.employee_id)?.name || 'Unknown',
        actual_hours: parseFloat(record.actual_hours?.toString() || '0'),
        gross_pay: parseFloat(record.gross_pay?.toString() || '0'),
        net_pay: parseFloat(record.net_pay?.toString() || '0'),
        calculation_method: record.calculation_method || 'unknown',
        country_code: record.country_code,
        auto_calculated: record.auto_calculated || false,
        created_at: record.created_at || '',
        status: record.status || 'draft'
      }));

      setEmployeesWithPayroll(employeesWithPayrollData);
      setPayrollRecords(records);
      setHasExistingPayroll(records.length > 0);
      setInitialLoadComplete(true);
    } catch (error) {
      toast.error('Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  }, [business?.id, selectedPeriod, effectiveCountryCode, allEmployeesWithStatus]);

  // Handle period navigation with audit logging
  const handlePeriodChange = useCallback(async (newPeriod: PayrollPeriod) => {
    const previousPeriod = selectedPeriod;
    
    // Log period navigation
    await auditService.logPeriodNavigation(previousPeriod, newPeriod);
    
    // Add to recently viewed in history
    const totalEmployees = employeesWithPayroll.length;
    payrollHistory.addToRecentlyViewed(newPeriod, {
      period: newPeriod,
      status: 'draft',
      isFavorite: payrollHistory.isFavorite(newPeriod),
      hasChanges: false,
      totalEmployees: totalEmployees,
      completionPercentage: 0
    });
    
    setSelectedPeriod(newPeriod);
  }, [selectedPeriod, auditService, payrollHistory, employeesWithPayroll]);

  // Handle payroll actions with security confirmations
  const handlePayrollAction = useCallback(async (action: PayrollAction, period: PayrollPeriod) => {
    setPendingAction({ action, period });
    
    switch (action.id) {
      case 'finalize':
        setFinalizationDialog(true);
        break;
        
      case 'process_payments':
        setPaymentDialog(true);
        break;
        
      case 'export':
      case 'export_draft':
      case 'export_historical':
        await handleExport(action.id as any);
        break;
        
      default:
        // Handle other actions with generic security dialog
        setSecurityDialog({
          isOpen: true,
          title: action.label,
          description: action.confirmationMessage || `Are you sure you want to ${action.label.toLowerCase()}?`,
          action: () => performGenericAction(action)
        });
    }
  }, []);


  // Handle export actions
  const handleExport = useCallback(async (exportType: 'export' | 'export_draft' | 'export_historical') => {
    const employeeIds = employeesWithPayroll.map(emp => emp.employee_id);
    
    await auditService.logDataExport(selectedPeriod, 'csv', employeeIds);
    
    // Use existing export function
    exportToCSV();
    
    setPendingAction(null);
  }, [employeesWithPayroll, auditService, selectedPeriod]);

  // Perform generic action
  const performGenericAction = useCallback(async (action: PayrollAction) => {
    await auditService.logCustomAction({
      action: 'security_action',
      period: selectedPeriod,
      description: `Performed action: ${action.label}`,
      actionData: { actionId: action.id },
      severity: 'medium'
    });
    
    // Handle specific action logic here
    toast.success(`${action.label} completed`);
    
    setSecurityDialog({ isOpen: false, title: '', description: '' });
    setPendingAction(null);
  }, [auditService, selectedPeriod]);

  // Load employees with payroll data when component mounts or dependencies change
  useEffect(() => {
    loadEmployeesWithPayroll();
  }, [loadEmployeesWithPayroll]);

  // Load planning data for period selector
  useEffect(() => {
    const loadPlanningData = async () => {
      if (!business?.id) return;
      
      try {
        const summary = await getBusinessPlanningSummary(
          business.id,
          selectedPeriod.year,
          selectedPeriod.month
        );
        
        setPlanningSummary({
          employeesWithShifts: summary.employeesWithShifts,
          totalEmployees: summary.totalEmployees,
          totalPlannedHours: summary.totalPlannedHours,
          totalProjectedGrossPay: summary.totalProjectedGrossPay,
          overScheduledEmployees: summary.overScheduledEmployees,
          underScheduledEmployees: summary.underScheduledEmployees,
        });
        
        // Calculate compliance rate
        const rate = summary.totalEmployees > 0 ? 
          ((summary.totalEmployees - summary.overScheduledEmployees - summary.underScheduledEmployees) / summary.totalEmployees) * 100 : 100;
        setComplianceRate(rate);
        
      } catch (err) {
        console.error('Failed to load planning data for period selector:', err);
        setPlanningSummary(null);
        setComplianceRate(0);
      }
    };

    loadPlanningData();
  }, [business?.id, selectedPeriod]);

  // Calculate payment summary data from payroll records
  useEffect(() => {
    if (payrollRecords.length > 0) {
      const totalEmployeePayments = payrollRecords.reduce((sum, record) => sum + record.net_pay, 0);
      const totalEmployees = payrollRecords.length;
      const pendingPayments = payrollRecords.filter(record => record.status === 'pending').length;
      const completedPayments = payrollRecords.filter(record => record.status === 'approved').length;

      setPaymentSummary({
        totalEmployeePayments,
        totalEmployees,
        pendingPayments,
        completedPayments
      });
    } else {
      setPaymentSummary(null);
    }
  }, [payrollRecords]);

  // Create consolidated statistics - eliminates duplicates and provides single source of truth
  useEffect(() => {
    // Only create consolidated stats if we have at least some data
    if (!planningSummary && !paymentSummary && payrollRecords.length === 0) {
      setConsolidatedStats(null);
      return;
    }

    // Determine the single source of truth for employee count
    const totalEmployees = planningSummary?.totalEmployees || 
                          paymentSummary?.totalEmployees || 
                          employeesWithPayroll.length || 0;
    
    // Calculate employee coverage
    const employeesWithPayrollRecords = employeesWithPayroll.filter(emp => emp.has_payroll_record).length;
    const employeesWithShifts = planningSummary?.employeesWithShifts || 0;
    
    // Calculate financial data - prefer actual over planned
    const actualGrossPay = payrollRecords.length > 0 ? 
      payrollRecords.reduce((sum, record) => sum + record.gross_pay, 0) : undefined;
    const plannedGrossPay = planningSummary?.totalProjectedGrossPay;
    
    const netPay = paymentSummary?.totalEmployeePayments || 
                  payrollRecords.reduce((sum, record) => sum + record.net_pay, 0);
    
    // Calculate hours data - prefer actual over planned
    const actualHours = payrollRecords.length > 0 ? 
      payrollRecords.reduce((sum, record) => sum + record.actual_hours, 0) : undefined;
    const plannedHours = planningSummary?.totalPlannedHours;
    
    // Payment status data
    const pendingPayments = paymentSummary?.pendingPayments || 
                           payrollRecords.filter(record => record.status === 'pending').length;
    const completedPayments = paymentSummary?.completedPayments || 
                             payrollRecords.filter(record => record.status === 'approved').length;
    
    setConsolidatedStats({
      totalEmployees,
      employeeCoverage: {
        withPayroll: employeesWithPayrollRecords,
        withShifts: employeesWithShifts
      },
      grossPay: {
        planned: plannedGrossPay,
        actual: actualGrossPay
      },
      netPay,
      paymentStatus: {
        pending: pendingPayments,
        completed: completedPayments
      },
      hours: {
        planned: plannedHours,
        actual: actualHours
      },
      complianceRate,
      complianceIssues: {
        overScheduled: planningSummary?.overScheduledEmployees || 0,
        underScheduled: planningSummary?.underScheduledEmployees || 0
      }
    });
  }, [planningSummary, paymentSummary, complianceRate, payrollRecords, employeesWithPayroll]);

  const toggleRecordSelection = (recordId: string) => {
    setSelectedRecords(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(recordId)) {
        newSelection.delete(recordId);
      } else {
        newSelection.add(recordId);
      }
      return newSelection;
    });
  };

  const toggleSelectAll = () => {
    const employeesWithRecordsIds = employeesWithPayroll
      .filter(emp => emp.has_payroll_record && emp.payroll_record_id)
      .map(emp => emp.payroll_record_id!);
    
    if (selectedRecords.size === employeesWithRecordsIds.length) {
      setSelectedRecords(new Set());
    } else {
      setSelectedRecords(new Set(employeesWithRecordsIds));
    }
  };

  const generateSimplePayslip = async (record: PayrollRecord) => {
    try {
      // Get password for employee payslip based on their preferences
      const password = await getPayslipPassword(record.employee_id);
      
      // Prepare payslip data for the shared PDF generator
      const payslipData: PayslipData = {
        employee: {
          name: record.employee_name,
          id: record.employee_id,
        },
        business: {
          name: business?.name || 'Business Name',
          address: business?.full_address,
          phone: business?.phone,
          email: business?.email,
        },
        period: {
          start: startOfMonth(selectedPeriod.date),
          end: endOfMonth(selectedPeriod.date),
          month: selectedPeriod.date.getMonth() + 1,
          year: selectedPeriod.date.getFullYear(),
        },
        payment: {
          hoursWorked: record.actual_hours,
          grossPay: record.gross_pay,
          netPay: record.net_pay,
          calculationMethod: record.calculation_method,
          autoCalculated: record.auto_calculated,
        },
        location: {
          countryCode: record.country_code || effectiveCountryCode || undefined,
        },
      };

      // Generate and download the PDF with password protection
      await generatePayslipPDF(payslipData, {
        password: password,
        enablePasswordProtection: password !== null
      });
      
      if (password) {
        toast.success(`Password-protected PDF payslip generated for ${record.employee_name}`);
      } else {
        toast.success(`PDF payslip generated for ${record.employee_name}`);
      }
    } catch (error) {
      console.error('Error generating payslip PDF:', error);
      toast.error('Failed to generate payslip PDF');
    }
  };

  const exportToCSV = () => {
    if (employeesWithPayroll.length === 0) {
      toast.error('No employee data to export');
      return;
    }

    const headers = [
      'Employee Name',
      'Role',
      'Pay Rate',
      'Actual Hours', 
      'Gross Pay',
      'Net Pay',
      'Calculation Method',
      'Country',
      'Auto Calculated',
      'Status',
      'Has Payroll Record'
    ];

    const csvContent = [
      headers.join(','),
      ...employeesWithPayroll.map(employee => [
        employee.employee_name,
        employee.role_name || 'No Role',
        employee.pay_rate.toFixed(2),
        employee.actual_hours.toFixed(2),
        employee.gross_pay.toFixed(2),
        employee.net_pay.toFixed(2),
        employee.calculation_method,
        employee.country_code || 'N/A',
        employee.auto_calculated ? 'Yes' : 'No',
        employee.status,
        employee.has_payroll_record ? 'Yes' : 'No'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `payroll-${format(selectedPeriod.date, 'yyyy-MM')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Employee payroll data exported successfully');
  };

  const generatePaymentSummary = () => {
    const selectedPayrollRecords = payrollRecords.filter(record => selectedRecords.has(record.id));
    
    if (selectedPayrollRecords.length === 0) {
      toast.error('Please select at least one payroll record');
      return;
    }

    const totalGrossPay = selectedPayrollRecords.reduce((sum, record) => sum + record.gross_pay, 0);
    const totalNetPay = selectedPayrollRecords.reduce((sum, record) => sum + record.net_pay, 0);
    const totalHours = selectedPayrollRecords.reduce((sum, record) => sum + record.actual_hours, 0);

    const paymentSummaryHTML = `
      <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px;">
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="color: #333; margin-bottom: 10px;">PAYMENT SUMMARY</h1>
          <h2 style="color: #666; font-weight: normal;">${business?.name || 'Business Name'}</h2>
          <p style="margin: 5px 0;">Period: ${format(selectedPeriod.date, 'MMMM yyyy')}</p>
          <p style="margin: 5px 0;">Generated on: ${format(new Date(), 'dd/MM/yyyy HH:mm')}</p>
        </div>
        
        <div style="border: 2px solid #ddd; padding: 20px; margin-bottom: 20px;">
          <h3 style="color: #333; border-bottom: 1px solid #ddd; padding-bottom: 10px;">Summary</h3>
          <table style="width: 100%; border-collapse: collapse;">
            <tr style="background-color: #f5f5f5;">
              <td style="padding: 10px; border: 1px solid #ddd; font-weight: bold;">Total Employees</td>
              <td style="padding: 10px; border: 1px solid #ddd; text-align: right;">${selectedPayrollRecords.length}</td>
            </tr>
            <tr>
              <td style="padding: 10px; border: 1px solid #ddd; font-weight: bold;">Total Hours</td>
              <td style="padding: 10px; border: 1px solid #ddd; text-align: right;">${totalHours.toFixed(1)}h</td>
            </tr>
            <tr>
              <td style="padding: 10px; border: 1px solid #ddd; font-weight: bold;">Total Gross Pay</td>
              <td style="padding: 10px; border: 1px solid #ddd; text-align: right;">${effectiveCountryCode ? formatEUR(totalGrossPay) : `$${totalGrossPay.toFixed(2)}`}</td>
            </tr>
            <tr style="background-color: #f9f9f9; font-weight: bold;">
              <td style="padding: 10px; border: 1px solid #ddd;">Total Net Pay</td>
              <td style="padding: 10px; border: 1px solid #ddd; text-align: right; color: #16a34a;">${effectiveCountryCode ? formatEUR(totalNetPay) : `$${totalNetPay.toFixed(2)}`}</td>
            </tr>
          </table>
        </div>

        <div style="border: 2px solid #ddd; padding: 20px;">
          <h3 style="color: #333; border-bottom: 1px solid #ddd; padding-bottom: 10px;">Employee Details</h3>
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="background-color: #f5f5f5;">
                <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Employee</th>
                <th style="padding: 8px; border: 1px solid #ddd; text-align: right;">Hours</th>
                <th style="padding: 8px; border: 1px solid #ddd; text-align: right;">Gross Pay</th>
                <th style="padding: 8px; border: 1px solid #ddd; text-align: right;">Net Pay</th>
              </tr>
            </thead>
            <tbody>
              ${selectedPayrollRecords.map(record => `
                <tr>
                  <td style="padding: 8px; border: 1px solid #ddd;">${record.employee_name}</td>
                  <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${record.actual_hours.toFixed(1)}h</td>
                  <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${effectiveCountryCode ? formatEUR(record.gross_pay) : `$${record.gross_pay.toFixed(2)}`}</td>
                  <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${effectiveCountryCode ? formatEUR(record.net_pay) : `$${record.net_pay.toFixed(2)}`}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      </div>
    `;

    // Open payment summary in new window
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(paymentSummaryHTML);
      newWindow.document.close();
      newWindow.focus();
    }
    
    toast.success(`Payment summary generated for ${selectedPayrollRecords.length} employees`);
  };

  // Determine if business uses Euro
  const useEuro = business?.country_code === 'SK' || business?.country_code === 'CZ' || business?.country_code === 'AT';
  const formatCurrency = (amount: number) => useEuro ? formatEUR(amount) : `$${amount.toFixed(2)}`;

  // Convert employees to payment items for the progress summary
  const employeePayments: PaymentItem[] = useMemo(() => {
    const baseStage = getStageFromMonthStatus(currentMonthStatus, payrollRecords.length > 0);

    return employeesWithPayroll.map(employee => {
      // Check for Stripe payment status
      const stripePayment = payrollPayments.payments?.find(
        (p: PayrollPayment) => p.payroll_id === employee.payroll_record_id
      );

      const stage = stripePayment
        ? getStageFromPaymentStatus(stripePayment.status)
        : employee.has_payroll_record ? baseStage : 'upcoming';

      return {
        id: employee.payroll_record_id || employee.employee_id,
        paymentType: 'employee' as const,
        employeeId: employee.employee_id,
        employeeName: employee.employee_name,
        amount: employee.net_pay,
        grossAmount: employee.gross_pay,
        hoursWorked: employee.actual_hours,
        currency: useEuro ? 'EUR' : 'USD',
        stage,
        payrollId: employee.payroll_record_id,
        stripePaymentId: stripePayment?.stripe_transfer_id,
        failureReason: stripePayment?.failure_reason,
        payrollStatus: employee.status,
        paymentStatus: stripePayment?.status,
      };
    });
  }, [employeesWithPayroll, currentMonthStatus, payrollPayments.payments, payrollRecords.length, useEuro]);

  // Filter employees by selected payment stage
  const filteredEmployees = useMemo(() => {
    if (!selectedPaymentStage) return employeesWithPayroll;

    return employeesWithPayroll.filter(emp => {
      const payment = employeePayments.find(p => p.employeeId === emp.employee_id);
      return payment?.stage === selectedPaymentStage;
    });
  }, [employeesWithPayroll, employeePayments, selectedPaymentStage]);

  // Helper to get payment data for an employee
  const getEmployeePaymentData = (employeeId: string) => {
    return employeePayments.find(p => p.employeeId === employeeId);
  };

  // Calculate failed payments count for alerts
  const failedPaymentsCount = useMemo(() => {
    return employeePayments.filter(p => p.stage === 'failed').length;
  }, [employeePayments]);

  // Handler to view failed payments (filters to failed stage)
  const handleViewFailedPayments = () => {
    setSelectedPaymentStage('failed');
  };

  // Process single employee payment
  const handleProcessSinglePayment = async (employee: EmployeeWithPayroll) => {
    if (!employee.payroll_record_id || !business?.id) return;

    setProcessingEmployeeId(employee.employee_id);
    try {
      await payrollPayments.initiateTransfers(business.id, [employee.payroll_record_id]);
      toast.success('Payment initiated', {
        description: `Payment for ${employee.employee_name} is being processed`
      });
    } catch (error) {
      toast.error('Failed to process payment', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setProcessingEmployeeId(null);
    }
  };

  // Retry failed payment
  const handleRetryPayment = async (employee: EmployeeWithPayroll) => {
    if (!employee.payroll_record_id || !business?.id) return;

    setProcessingEmployeeId(employee.employee_id);
    try {
      await payrollPayments.retryFailedPayments(business.id, employee.payroll_record_id);
      toast.success('Retrying payment', {
        description: `Payment for ${employee.employee_name} is being retried`
      });
    } catch (error) {
      toast.error('Failed to retry payment', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setProcessingEmployeeId(null);
    }
  };

  // Process all ready payments
  const handleProcessAllReadyPayments = async () => {
    const readyPayments = employeePayments.filter(p => p.stage === 'ready' && p.payrollId);
    if (readyPayments.length === 0 || !business?.id) return;

    try {
      await payrollPayments.initiateTransfers(
        business.id,
        readyPayments.map(p => p.payrollId!).filter(Boolean)
      );
      toast.success('Payments initiated', {
        description: `${readyPayments.length} payment(s) are being processed`
      });
    } catch (error) {
      toast.error('Failed to process payments', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  };

  // Bulk QR code generation
  const handleGenerateBulkQRCodes = () => {
    const readyPayments = employeePayments.filter(p => p.stage === 'ready');
    if (readyPayments.length === 0) {
      toast.error('No payments ready', {
        description: 'There are no payments in the "Ready" stage to generate QR codes for'
      });
      return;
    }

    // For now just show a success message - the actual QR dialog integration can be added later
    toast.success(`${readyPayments.length} payments ready for QR code generation`);
  };


  if (loadingData) {
    return (
      <div className="text-center py-8">
        <p>Loading payroll system...</p>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="text-center py-8">
        <p>Please select a business to view payroll data.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Smart Period Selection with Status Recognition - Global for all tabs */}
      <PayrollPeriodSelector
        selectedPeriod={selectedPeriod}
        onPeriodChange={handlePeriodChange}
        monthStatuses={monthStatuses}
        countryCode={effectiveCountryCode}
        business={business}
        consolidatedStats={consolidatedStats}
        selectedPeriodStatus={currentMonthStatus}
      />

      {/* Unified Status Alerts - Consolidates all warnings and messages */}
      <PayrollStatusAlerts
        monthWarnings={currentMonthStatus?.warnings || []}
        effectiveCountryCode={effectiveCountryCode}
        needingSetupCount={needingSetupCount}
        totalEmployees={employeesWithPayroll.length}
        employeesWithRecords={employeesWithPayroll.filter(emp => emp.has_payroll_record).length}
        failedPaymentsCount={failedPaymentsCount}
        onViewFailedPayments={handleViewFailedPayments}
      />

      {/* Payroll View - Show actual payroll data and calculations */}
      <div className="space-y-6">
          {/* Payment Progress Summary - Shows payment status overview */}
          {employeesWithPayroll.length > 0 && (
            <PaymentProgressSummary
              payments={employeePayments}
              selectedStage={selectedPaymentStage}
              onStageSelect={setSelectedPaymentStage}
              onBulkExportQR={handleGenerateBulkQRCodes}
              onProcessAllReady={hasStripePayments ? handleProcessAllReadyPayments : undefined}
              formatCurrency={formatCurrency}
              stripeEnabled={hasStripePayments}
              disabled={getPaymentAvailabilityForStatus(currentMonthStatus, payrollRecords.length > 0).readOnlyMode}
              isProcessing={payrollPayments.loading || payrollPayments.initiating}
            />
          )}

          {/* Filter indicator when a stage is selected */}
          {selectedPaymentStage && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Showing {filteredEmployees.length} employee{filteredEmployees.length !== 1 ? 's' : ''} with status:</span>
              <Badge variant="outline">{selectedPaymentStage}</Badge>
            </div>
          )}

          {/* Employee Cards with Integrated Payroll, QR Codes & Payslips */}
          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 4 }, (_, i) => (
                <Card key={i} className="p-6">
                  <div className="flex items-center space-x-6">
                    <div className="flex items-center space-x-4 flex-1">
                      <Skeleton className="h-12 w-12 rounded-full" />
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <Skeleton className="h-16 w-16" />
                      <div className="space-y-2">
                        <Skeleton className="h-8 w-24" />
                        <Skeleton className="h-8 w-24" />
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : employeesWithPayroll.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium text-muted-foreground">
                No employees found for this business.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Add employees to start managing payroll.
              </p>
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium text-muted-foreground">
                No employees match the selected filter.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Try selecting a different payment status or clear the filter.
              </p>
            </div>
          ) : (
            <EmployeePayrollTable
              employees={filteredEmployees}
              fulfillmentData={fulfillmentData}
              currentMonthStatus={currentMonthStatus}
              selectedPeriod={selectedPeriod}
              business={business}
              onPayslipGenerate={(employee) => generateSimplePayslip({
                id: employee.payroll_record_id!,
                employee_id: employee.employee_id,
                employee_name: employee.employee_name,
                actual_hours: employee.actual_hours,
                gross_pay: employee.gross_pay,
                net_pay: employee.net_pay,
                calculation_method: employee.calculation_method,
                country_code: employee.country_code,
                auto_calculated: employee.auto_calculated,
                created_at: employee.created_at || '',
                status: employee.status
              })}
              getPaymentAvailability={(employeeId) => {
                const emp = filteredEmployees.find(e => e.employee_id === employeeId);
                return getPaymentAvailabilityForStatus(
                  currentMonthStatus,
                  emp?.has_payroll_record || false
                );
              }}
              getPaymentData={getEmployeePaymentData}
              onRetryPayment={handleRetryPayment}
              onProcessPayment={handleProcessSinglePayment}
              stripeEnabled={hasStripePayments}
              processingEmployeeId={processingEmployeeId}
            />
          )}
      </div>

      {/* Security Confirmation Dialogs */}
      {currentMonthStatus && pendingAction && (
        <>
          <PayrollFinalizationConfirmation
            isOpen={finalizationDialog}
            onClose={() => {
              setFinalizationDialog(false);
              setPendingAction(null);
            }}
            onConfirm={async () => {
              await auditService.logCustomAction({
                action: 'finalize',
                period: selectedPeriod,
                description: 'Payroll finalized - locked for editing',
                severity: 'critical'
              });
              toast.success('Payroll finalized');
              setFinalizationDialog(false);
              setPendingAction(null);
            }}
            period={selectedPeriod}
            monthStatus={currentMonthStatus}
            totalGrossPay={employeesWithPayroll.reduce((sum, employee) => sum + employee.gross_pay, 0)}
            totalNetPay={employeesWithPayroll.reduce((sum, emp) => sum + emp.net_pay, 0)}
            employeeCount={employeesWithPayroll.filter(emp => emp.has_payroll_record).length}
            countryCode={effectiveCountryCode}
          />

          <PaymentProcessingConfirmation
            isOpen={paymentDialog}
            onClose={() => {
              setPaymentDialog(false);
              setPendingAction(null);
            }}
            onConfirm={async () => {
              const payments = employeesWithPayroll
                .filter(emp => emp.has_payroll_record)
                .map(emp => ({
                  employeeId: emp.employee_id,
                  amount: emp.net_pay,
                  paymentMethod: 'bank_transfer'
                }));
              
              await auditService.logPaymentProcessing(
                selectedPeriod,
                payments,
                employeesWithPayroll.reduce((sum, emp) => sum + emp.net_pay, 0)
              );
              
              toast.success('Payments processed');
              setPaymentDialog(false);
              setPendingAction(null);
            }}
            period={selectedPeriod}
            payments={employeesWithPayroll
              .filter(emp => emp.has_payroll_record)
              .map(emp => ({
                employeeName: emp.employee_name,
                amount: emp.net_pay,
                accountInfo: 'Bank Transfer'
              }))}
            totalAmount={employeesWithPayroll.reduce((sum, emp) => sum + emp.net_pay, 0)}
            countryCode={effectiveCountryCode}
          />

          <SecurityConfirmationDialog
            isOpen={securityDialog.isOpen}
            onClose={() => setSecurityDialog({ isOpen: false, title: '', description: '' })}
            onConfirm={securityDialog.action || (() => {})}
            title={securityDialog.title}
            description={securityDialog.description}
            requirePassword={pendingAction?.action.id === 'finalize' || pendingAction?.action.id === 'process_payments'}
            criticalAction={pendingAction?.action.id === 'finalize' || pendingAction?.action.id === 'process_payments'}
          />
        </>
      )}
    </div>
  );
};

// Main exported component with provider wrapper
export const UnifiedPayrollDashboard: React.FC<UnifiedPayrollDashboardProps> = (props) => {
  return (
    <PayrollHistoryProvider businessId={props.business?.id}>
      <UnifiedPayrollDashboardInternal {...props} />
    </PayrollHistoryProvider>
  );
};
